/**
 * Ported from yaqatw's src/service/cloud/nextcloud.ts. WebDAV/OAuth2 logic is
 * unchanged; every `SettingsXml.getItem/addItem` call becomes
 * `getSettingValue`/`useSettingsStore().setValue` against qda's own
 * "backend" settings page (src/settings/pages/backend.ts) instead of
 * yaqatw's SettingsXml.
 */
import Events from "./events";
import { BaseCloudProvider } from "./BaseCloudProvider";
import type { FileMetadata, PushResult, UploadResult, OAuthDialogMessage } from "./types";
import { log } from "./log";
import { getSettingValue, useSettingsStore } from "../settings/settingsStore";

function getBackendSetting(key: string): string {
  const value = getSettingValue("backend", key);
  return typeof value === "string" ? value : "";
}

class NextcloudClass extends BaseCloudProvider {
  public readonly tokenName = "nextcloudOAuthToken";
  public readonly refreshTokenName = "nextcloudOAuthRefresh";
  protected readonly providerName = "Nextcloud";

  private appFolderPromise: Promise<string> | null = null;
  private folderCache: Map<string, string> = new Map();
  private folderResolutionPromises: Map<string, Promise<string>> = new Map();

  // OAuth2 properties
  private serverUrl: string = "";
  private clientId: string = "";
  private clientSecret: string = "";
  private accessToken: string = "";
  private refreshToken: string = "";

  protected readonly MIN_REQUEST_INTERVAL_MS = 300;

  /**
   * Initializes OAuth configuration from settings.
   */
  async initOAuth(serverUrl?: string, clientId?: string, clientSecret?: string): Promise<void> {
    try {
      this.serverUrl = serverUrl || getBackendSetting("nextcloudUrl");
      this.clientId = clientId || getBackendSetting("nextcloudOAuthClientId");
      this.clientSecret = clientSecret || getBackendSetting("nextcloudOAuthClientSecret");

      const savedToken = localStorage.getItem(this.tokenName);
      const savedRefresh = localStorage.getItem(this.refreshTokenName);
      if (savedToken) this.accessToken = savedToken;
      if (savedRefresh) this.refreshToken = savedRefresh;
    } catch (error) {
      console.warn("Could not load OAuth configuration:", error);
      if (serverUrl) this.serverUrl = serverUrl;
      if (clientId) this.clientId = clientId;
      if (clientSecret) this.clientSecret = clientSecret;
    }
  }

  /**
   * Nextcloud uses authorization-code flow: the popup returns a `code`, we
   * exchange it for an access+refresh token pair via POST. Overrides the
   * base default (which expects implicit-grant tokens).
   */
  protected async performAuthenticate(): Promise<string> {
    if (!this.serverUrl || !this.clientId || !this.clientSecret) {
      throw new Error(
        "Nextcloud OAuth not configured. Please set serverUrl, clientId, and clientSecret in settings."
      );
    }

    const state = Math.random().toString(36).substring(7);
    const authUrl =
      `${this.serverUrl}/index.php/apps/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${encodeURIComponent(this.clientId)}&` +
      `redirect_uri=${encodeURIComponent(this.redirectUri)}&` +
      `state=${state}`;

    const result = await this.openAuthDialog(authUrl, { height: 600, width: 600 });
    if (!result.code) throw new Error("Nextcloud authenticate(): no code returned");
    await this.exchangeCodeForToken(result.code);
    return this.accessToken;
  }

  /**
   * Override the implicit-grant parser — Nextcloud postMessage carries `code`.
   */
  protected parseAuthMessage(message: OAuthDialogMessage): { token?: string; code?: string } {
    if (message.error) throw new Error(`Authentication failed: ${message.error}`);
    if (message.success && message.code) return { code: message.code };
    throw new Error("Auth dialog returned no code");
  }

  protected getAuthUrl(): string {
    // Built dynamically inside authenticate() since it depends on serverUrl.
    return "";
  }

  /**
   * Exchanges OAuth authorization code for access token.
   */
  private async exchangeCodeForToken(code: string): Promise<void> {
    const response = await fetch(`${this.serverUrl}/index.php/apps/oauth2/api/v1/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        client_id: this.clientId,
        client_secret: this.clientSecret,
        redirect_uri: this.redirectUri,
      }).toString(),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;

    localStorage.setItem(this.tokenName, this.accessToken);
    localStorage.setItem(this.refreshTokenName, this.refreshToken);
  }

  /**
   * Refreshes access token via refresh-token flow.
   */
  protected async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      throw new Error("No refresh token available. Please re-authenticate.");
    }

    const response = await fetch(`${this.serverUrl}/index.php/apps/oauth2/api/v1/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: this.refreshToken,
        client_id: this.clientId,
        client_secret: this.clientSecret,
      }).toString(),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    this.accessToken = data.access_token;
    if (data.refresh_token) this.refreshToken = data.refresh_token;

    localStorage.setItem(this.tokenName, this.accessToken);
    if (data.refresh_token) localStorage.setItem(this.refreshTokenName, this.refreshToken);

    return this.accessToken;
  }

  /**
   * Override base getAccessToken to use the in-memory token (refreshed via
   * refresh_token flow) rather than just localStorage.
   */
  protected async getAccessToken(): Promise<string> {
    if (this.accessToken) return this.accessToken;
    const savedToken = localStorage.getItem(this.tokenName);
    if (savedToken) {
      this.accessToken = savedToken;
      return savedToken;
    }
    await this.authenticate();
    return this.accessToken;
  }

  /**
   * Best-effort, non-interactive collaboration identity: `nextcloud:<host>:<user>`.
   * Prefers the cached `nextcloudUserName` (populated after the first sync) so
   * the common case needs no network call; otherwise queries OCS only if a token
   * is already present (getCurrentUser → fetchWithAuth refreshes silently or
   * throws — it never opens the auth popup here). Returns null when the user
   * isn't signed in or the server URL is unknown.
   */
  public async getIdentity(): Promise<string | null> {
    try {
      const server = (this.serverUrl || getBackendSetting("nextcloudUrl")).replace(/\/+$/, "");
      if (!server) return null;

      let user = getBackendSetting("nextcloudUserName");
      if (!user) {
        const haveToken = this.accessToken || localStorage.getItem(this.tokenName);
        if (!haveToken) return null;
        try {
          user = await this.getCurrentUser();
        } catch {
          user = "";
        }
      }
      if (!user) return null;

      let host = server;
      try {
        host = new URL(server).host;
      } catch {
        /* keep the raw string if it isn't a parseable URL */
      }
      return `nextcloud:${host}:${user}`;
    } catch {
      return null;
    }
  }

  /**
   * Gets the current authenticated user's username from settings or Nextcloud OCS API.
   */
  private async getCurrentUser(): Promise<string> {
    try {
      const settingsUsername = getBackendSetting("nextcloudUserName");
      if (settingsUsername) {
        return settingsUsername;
      }

      const config = await this.getConfig();
      const baseUrl = config.baseUrl.replace("/remote.php/dav/files/", "").replace(/\/$/, "");

      const response = await this.fetchWithAuth(
        `${baseUrl}/ocs/v2.php/apps/provisioning_api/api/v1/users/current?format=json`,
        { method: "GET" }
      );

      if (!response.ok) {
        console.warn(`OCS API returned status ${response.status}, trying alternative endpoint...`);
        const davResponse = await this.fetchWithAuth(`${baseUrl}/remote.php/dav/`, {
          method: "PROPFIND",
          headers: { Depth: "0" },
        });
        if (davResponse.ok) {
          console.warn("Could not determine username from OCS or settings, will use fallback path");
          return "";
        }
        throw new Error(`OCS API failed with status ${response.status}`);
      }

      const data = await response.json();

      let username = data?.ocs?.data?.id;
      if (!username) username = data?.data?.id || data?.id;
      if (!username) {
        console.error("OCS response did not contain expected username field:", data);
        throw new Error(`No username in OCS response: ${JSON.stringify(data)}`);
      }

      try {
        useSettingsStore.getState().setValue("backend", "nextcloudUserName", username);
      } catch (error) {
        console.warn("Could not save username to settings:", error);
      }

      return username;
    } catch (error) {
      console.error("Error getting current user:", error);
      throw new Error(`Could not determine Nextcloud username: ${(error as any).message}`);
    }
  }

  /**
   * Retrieves Nextcloud OAuth configuration from settings.
   */
  private async getConfig() {
    let serverUrl = this.serverUrl;
    const accessToken = await this.getAccessToken();

    if (!serverUrl) {
      serverUrl = getBackendSetting("nextcloudUrl");
    }

    if (!serverUrl || !accessToken) {
      throw new Error("Nextcloud OAuth not configured. Please authenticate first.");
    }

    let baseUrl = serverUrl;
    if (!baseUrl.endsWith("/")) baseUrl += "/";
    baseUrl = `${baseUrl}remote.php/dav/files/`;

    return { baseUrl, accessToken };
  }

  /**
   * Ensures the application folder exists in Nextcloud.
   */
  private async ensureAppFolder(): Promise<string> {
    if (this.appFolderPromise) return this.appFolderPromise;

    this.appFolderPromise = (async () => {
      const config = await this.getConfig();
      const folderName = "YaqatW-DATA";

      let folderUrl: string;
      try {
        const username = await this.getCurrentUser();
        folderUrl = username
          ? `${config.baseUrl}${encodeURIComponent(username)}/${folderName}/`
          : `${config.baseUrl}${folderName}/`;
      } catch (error) {
        console.warn("Could not get username, trying root WebDAV path:", error);
        folderUrl = `${config.baseUrl}${folderName}/`;
      }

      try {
        const checkResponse = await this.fetchWithAuth(folderUrl, {
          method: "PROPFIND",
          headers: { Depth: "0" },
        });
        if (checkResponse.ok) {
          log("App folder exists at:", folderUrl);
          return folderUrl;
        }

        const createResponse = await this.fetchWithAuth(folderUrl, { method: "MKCOL" });
        if (!createResponse.ok && createResponse.status !== 405) {
          throw new Error(`Failed to create app folder: ${createResponse.status}`);
        }
        log("App folder created at:", folderUrl);
        return folderUrl;
      } catch (error) {
        console.error("Error ensuring app folder:", error);
        this.appFolderPromise = null;
        throw error;
      }
    })();

    return this.appFolderPromise;
  }

  /**
   * Resolves the full URL to a file, creating parent folders if necessary.
   */
  private async resolvePath(
    fileName: string,
    isFolder: boolean = false,
    extension: string = ""
  ): Promise<string> {
    let currentUrl = await this.ensureAppFolder();
    if (!currentUrl.endsWith("/")) currentUrl += "/";

    const parts = fileName.split("/").filter((p) => p);

    if (isFolder) {
      for (const part of parts) {
        const nextUrl = `${currentUrl}${part}/`;
        if (!this.folderCache.has(nextUrl)) {
          const response = await this.fetchWithAuth(nextUrl, { method: "MKCOL" });
          if (response.ok || response.status === 405) {
            this.folderCache.set(nextUrl, nextUrl);
          } else {
            throw new Error(`Failed to create folder ${part}: ${response.status}`);
          }
        } else {
          this.folderCache.set(nextUrl, nextUrl);
        }
        currentUrl = nextUrl;
      }
      return currentUrl;
    }

    const fileBaseName = parts.pop();
    for (const part of parts) {
      const nextUrl = `${currentUrl}${part}/`;
      if (!this.folderCache.has(nextUrl)) {
        const response = await this.fetchWithAuth(nextUrl, { method: "MKCOL" });
        if (response.ok || response.status === 405) {
          this.folderCache.set(nextUrl, nextUrl);
        } else {
          throw new Error(`Failed to create folder ${part}: ${response.status}`);
        }
      } else {
        this.folderCache.set(nextUrl, nextUrl);
      }
      currentUrl = nextUrl;
    }
    return `${currentUrl}${fileBaseName}${extension}`;
  }

  public async pull(itemPath: string, fileId_: string | null = null): Promise<any> {
    try {
      const fullUrl = fileId_ ? fileId_ : await this.resolvePath(itemPath);

      const response = await this.fetchWithAuth(fullUrl, { method: "GET" });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Download failed with status ${response.status}`);
      }

      const fileContent = await response.text();
      if (!fileContent) return null;

      try {
        return JSON.parse(fileContent);
      } catch (parseError) {
        console.warn(`[pull] Failed to parse JSON for ${itemPath}:`, parseError);
        return null;
      }
    } catch (error: any) {
      if (error.message && (error.message.includes("not found") || error.message.includes("404"))) {
        console.warn(`[Sync] Suppressing known error for ${itemPath}: ${error.message}`);
        return null;
      }
      console.error(`Error pulling ${itemPath}:`, error);
      Events.emit("showError", `Sync failed: ${error.message}`);
      throw error;
    }
  }

  private async uploadToNextcloud(
    fileName: string,
    data: any,
    isRetry: boolean = false
  ): Promise<PushResult | void> {
    try {
      const fullUrl = await this.resolvePath(fileName);
      const fileContent = JSON.stringify(data);

      const response = await this.fetchWithAuth(fullUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: fileContent,
      });

      if (!response.ok) {
        if ((response.status === 404 || response.status === 409) && !isRetry) {
          console.warn(
            `[Nextcloud] Upload ${response.status} for ${fileName}, clearing folder cache and retrying...`
          );
          this.folderCache.clear();
          this.folderResolutionPromises.clear();
          this.appFolderPromise = null;
          return this.uploadToNextcloud(fileName, data, true);
        }
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const lastModifiedHeader = response.headers.get("Last-Modified");
      if (lastModifiedHeader) {
        return { modifiedTime: new Date(lastModifiedHeader).toISOString() };
      }
    } catch (error: any) {
      console.error(`Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  public async push(fileName: string, data: any): Promise<PushResult | void> {
    return this.uploadToNextcloud(fileName, data);
  }

  public async uploadFile(
    filePath: string,
    fileContent: Blob | string | ArrayBuffer
  ): Promise<UploadResult> {
    try {
      const fullUrl = await this.resolvePath(filePath, false, "");

      let bodyContent: string | ArrayBuffer | Blob;
      let contentType = "application/octet-stream";

      if (fileContent instanceof Blob) {
        bodyContent = fileContent;
        contentType = fileContent.type || "application/octet-stream";
      } else if (fileContent instanceof ArrayBuffer) {
        bodyContent = fileContent;
      } else if (typeof fileContent === "string") {
        bodyContent = fileContent;
        if (filePath.endsWith(".json")) contentType = "application/json";
        else if (filePath.endsWith(".csv")) contentType = "text/csv";
        else if (filePath.endsWith(".html") || filePath.endsWith(".htm")) contentType = "text/html";
        else if (filePath.endsWith(".xml")) contentType = "application/xml";
        else contentType = "text/plain";
      } else {
        bodyContent = String(fileContent);
        contentType = "text/plain";
      }

      const response = await this.fetchWithAuth(fullUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: bodyContent,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      return { url: fullUrl };
    } catch (error: any) {
      console.error(`Error uploading file ${filePath}:`, error);
      Events.emit("showError", `File upload failed: ${error.message}`);
      throw error;
    }
  }

  public async downloadFile(filePath: string): Promise<Blob> {
    const fullUrl = await this.resolvePath(filePath);
    const response = await this.fetchWithAuth(fullUrl, { method: "GET" });
    if (!response.ok) {
      throw new Error(`Nextcloud download failed for ${filePath}: ${response.status}`);
    }
    return response.blob();
  }

  public async delete(filePath: string): Promise<void> {
    try {
      const fullUrl = await this.resolvePath(filePath);

      const response = await this.fetchWithAuth(fullUrl, { method: "DELETE" });

      if (!response.ok && response.status !== 404) {
        throw new Error(`Delete failed with status ${response.status}`);
      }

      if (response.status === 404) {
        console.warn(`File not found for deletion: ${filePath}`);
        return;
      }

      log(`Successfully deleted ${filePath} from Nextcloud`);
    } catch (error) {
      console.error(`Error deleting ${filePath}:`, error);
      Events.emit("showError", `Delete failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pushDiff(globalEntries: any[]): Promise<void> {
    for (const entry of globalEntries) {
      await this.push(entry.path, entry.data);
      Events.emit("showStatus", { intent: "info", text: `Synced ${entry.path} to Nextcloud.` });
    }
  }

  public async pullById(fileId: string): Promise<any> {
    try {
      const response = await this.fetchWithAuth(fileId, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Download by ID failed with status ${response.status}`);
      }
      const fileContent = await response.text();
      return JSON.parse(fileContent);
    } catch (error) {
      console.error(`Error pulling by ID ${fileId}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async getMetadataAll(projectName: string): Promise<FileMetadata[]> {
    const allFiles: FileMetadata[] = [];
    try {
      log("[getMetadataAll] Starting for:", projectName);
      const config = await this.getConfig();
      const serverBaseUrl = config.baseUrl.replace("/remote.php/dav/files/", "").replace(/\/$/, "");

      const normalizedProjectName = projectName.replace(/\/$/, "");

      const traverse = async (folderPath: string, _relPath: string = normalizedProjectName) => {
        log("[getMetadataAll] Traversing:", folderPath);
        const listResponse = await this.fetchWithAuth(folderPath, {
          method: "PROPFIND",
          headers: { Depth: "1", "Content-Type": "application/xml" },
          body: `<?xml version="1.0" encoding="UTF-8"?>
                        <d:propfind xmlns:d="DAV:">
                            <d:prop>
                                <d:displayname/>
                                <d:resourcetype/>
                                <d:getlastmodified/>
                            </d:prop>
                        </d:propfind>`,
        });

        if (listResponse.status === 404) {
          log(`Project folder not found: ${folderPath} (empty project)`);
          return;
        }
        if (!listResponse.ok) {
          throw new Error(`List files failed with status ${listResponse.status}`);
        }

        const xmlText = await listResponse.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, "application/xml");
        const responses = Array.from(xml.getElementsByTagName("d:response"));

        for (const response of responses) {
          const hrefElement = response.getElementsByTagName("d:href")[0];
          const lastModElement = response.getElementsByTagName("d:getlastmodified")[0];
          const resourceType = response.getElementsByTagName("d:resourcetype")[0];

          if (!hrefElement) continue;

          const href = hrefElement.textContent || "";
          const lastMod = lastModElement?.textContent || "";
          const isFolder = resourceType?.getElementsByTagName("d:collection").length > 0;
          const decodedHref = decodeURIComponent(href);

          if (decodedHref.endsWith(normalizedProjectName + "/")) continue;

          const normalizedHref = decodedHref.replace(/\/$/, "");
          const normalizedFolderPath = folderPath.replace(/\/$/, "").split("/").pop() || "";
          if (normalizedHref.endsWith(normalizedFolderPath) && isFolder) continue;

          if (isFolder) {
            const absoluteFolderUrl = decodedHref.startsWith("http")
              ? decodedHref
              : `${serverBaseUrl}${decodedHref}`;
            const folderName =
              decodedHref
                .split("/")
                .filter((p) => p)
                .pop() || "";
            await traverse(absoluteFolderUrl, _relPath + "/" + folderName);
          } else {
            const pathParts = decodedHref.split("/").filter((p) => p);
            const projectIndex = pathParts.findIndex((p) => p === normalizedProjectName);
            if (projectIndex >= 0) {
              const path = pathParts.slice(projectIndex).join("/");
              const fileName = decodedHref.split("/").pop() || "";
              allFiles.push({
                id: decodedHref.startsWith("http") ? decodedHref : `${serverBaseUrl}${decodedHref}`,
                name: fileName,
                path: path,
                modifiedTime: lastMod ? new Date(lastMod).toISOString() : new Date().toISOString(),
              });
            }
          }
        }
      };

      const projectFolder = await this.resolvePath(normalizedProjectName, true);
      log("[getMetadataAll] Starting traverse from:", projectFolder);
      await traverse(projectFolder);
      log("[getMetadataAll] Completed. Found", allFiles.length, "files");
    } catch (error) {
      console.error(`Error getting metadata for all files under ${projectName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
    return allFiles;
  }

  public async getMetadata(fileName: string): Promise<FileMetadata[]> {
    try {
      const fullUrl = await this.resolvePath(fileName);
      const folderUrl = fullUrl.substring(0, fullUrl.lastIndexOf("/"));

      const listResponse = await this.fetchWithAuth(folderUrl, {
        method: "PROPFIND",
        headers: { Depth: "1", "Content-Type": "application/xml" },
        body: `<?xml version="1.0" encoding="UTF-8"?>
                    <d:propfind xmlns:d="DAV:">
                        <d:prop>
                            <d:displayname/>
                            <d:resourcetype/>
                            <d:getlastmodified/>
                        </d:prop>
                    </d:propfind>`,
      });

      if (listResponse.status === 404) {
        log(`Folder not found: ${folderUrl} (empty folder)`);
        return [];
      }
      if (!listResponse.ok) {
        throw new Error(`List files failed with status ${listResponse.status}`);
      }

      const text = await listResponse.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, "application/xml");
      const responses = Array.from(xml.getElementsByTagName("d:response"));

      const files: FileMetadata[] = [];
      for (const response of responses) {
        const hrefElement = response.getElementsByTagName("d:href")[0];
        const resourceType = response.getElementsByTagName("d:resourcetype")[0];
        const lastModified = response.getElementsByTagName("d:getlastmodified")[0];
        const isFolder = resourceType?.getElementsByTagName("d:collection").length > 0;

        if (!isFolder && hrefElement) {
          const href = decodeURIComponent(hrefElement.textContent || "");
          const displayName = response.getElementsByTagName("d:displayname")[0]?.textContent || "";
          if (displayName) {
            files.push({
              id: href,
              name: displayName,
              path: displayName,
              modifiedTime: lastModified?.textContent || new Date().toISOString(),
            });
          }
        }
      }
      return files;
    } catch (error) {
      console.error(`Error getting metadata for ${fileName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }
}

const Nextcloud = new NextcloudClass();
export default Nextcloud;
